import React from "react";
import { Pencil } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { INFRA_ITEMS, SOCIAL_ITEMS, STEPS } from "@/mock/data";
import { maskId } from "@/lib/mask";
import { fileLabel } from "@/components/steps/FormSteps4";

// value formatter
function fmt(v) {
  if (v === undefined || v === null || v === "" || v === "-- निवडा --") return null;
  if (typeof v === "object") return v.name;
  if (v === "yes") return "आहे";
  if (v === "no") return "नाही";
  return String(v);
}

function Row({ label, value }) {
  const shown = fmt(value);
  return (
    <div className="flex flex-col border-b border-dotted border-slate-200 py-2 sm:flex-row sm:gap-3">
      <span className="min-w-[220px] text-[13.5px] text-slate-500">{label}:</span>
      <span className={`text-[14px] font-semibold ${shown ? "text-slate-800" : "italic text-[#c2570a]"}`}>
        {shown || "रिकामे"}
      </span>
    </div>
  );
}

function Section({ index, title, children }) {
  const { goToStep } = useApp();
  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-4 py-3">
        <h3 className="text-[15px] font-bold text-[#1b5e3f]">{index}. {title}</h3>
        <button
          type="button"
          onClick={() => goToStep(index)}
          className="flex items-center gap-1.5 rounded-md border border-[#E0A106] bg-white px-3 py-1 text-[13px] font-medium text-[#b8790a] transition-colors hover:bg-orange-50"
        >
          <Pencil className="h-3.5 w-3.5" /> संपादित करा
        </button>
      </div>
      <div className="grid grid-cols-1 gap-x-8 px-4 py-3">{children}</div>
    </div>
  );
}

export default function PreviewStep({ errors = {} }) {
  const { application, setField } = useApp();
  const d = application.data;

  const todayStr = React.useMemo(() => {
    const t = new Date();
    const dd = String(t.getDate()).padStart(2, "0");
    const mm = String(t.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${t.getFullYear()}`;
  }, []);

  const nameMrEff = d.nameMr || [d.nameMrFirst, d.nameMrMiddle, d.nameMrLast].filter((p) => p && String(p).trim()).join(" ").trim();
  const signNameVal = nameMrEff || d.sign_name || "";
  const signMobileVal = d.mobile || d.sign_mobile || "";
  const declDateVal = d.decl_date ?? todayStr;

  return (
    <div className="animate-fade-up">
      <div className="mb-5 rounded-lg border border-[#1b5e3f]/20 bg-[#1b5e3f]/[0.04] px-4 py-4 text-center">
        <h2 className="text-[17px] font-bold text-[#1b5e3f]">जिल्हाधिकारी कार्यालय, सांगली</h2>
        <p className="mt-1 text-[15px] font-semibold text-[#c2570a]">आपले सरकार सेवा केंद्र मिळण्यासाठीचा अर्ज</p>
      </div>

      <p className="mb-4 text-[13.5px] text-slate-600">
        सादर करण्यापूर्वी खालील सर्व माहिती तपासा. दुरुस्ती आवश्यक असल्यास संबंधित टप्प्यासमोरील "संपादित करा" बटणावर क्लिक करा.
      </p>

      <Section index={1} title="वैयक्तिक माहिती">
        <Row label="अर्ज करावयाच्या स्थानिक स्वराज्य संस्थेचा प्रकार" value={d.orgType} />
        <Row label="तालुका (संस्था)" value={d.taluka} />
        <Row label="मंडळ / सर्कल कार्यालय" value={(d.circle || "").includes("::") ? d.circle.split("::").pop() : d.circle} />
        <Row label="स्थानिक स्वराज्य संस्था (ग्रामपंचायत / गाव)" value={d.village} />
        {d.lbWard && <Row label="वॉर्ड / प्रभाग क्रमांक" value={d.lbWard} />}
        <Row label="नाव (मराठी)" value={d.nameMr} />
        <Row label="नाव (इंग्रजी)" value={d.nameEn} />
        <Row label="जन्म तारीख" value={d.dob} />
        <Row label="वय" value={d.age} />
        <Row label="लिंग" value={d.gender} />
        <Row label="मोबाईल" value={d.mobile} />
        <Row label="ई-मेल" value={d.email} />
        <Row label="आधार क्रमांक" value={maskId(d.aadhaar)} />
        <Row label="आधारकार्ड अपलोड" value={d.aadhaar_file} />
        <Row label="पॅन क्रमांक" value={maskId(d.pan)} />
        <Row label="पॅनकार्ड अपलोड" value={d.pan_file} />
        <Row label="नाव बदल गॅझेट / विवाह दाखला" value={d.name_change_file} />
        <Row label="रहिवासाचा पत्ता" value={d.address} />
        <Row label="गाव/शहर" value={d.city} />
        <Row label="वॉर्ड क्रमांक" value={d.wardNo} />
        <Row label="तालुका" value={d.talukaRO || d.taluka} />
        <Row label="जिल्हा" value={d.districtRO} />
        <Row label="पिनकोड" value={d.pincode} />
        <Row label="रहिवासी पत्त्याचा पुरावा" value={d.residence_proof_file} />
        <Row label="स्वघोषणापत्र" value={d.self_declaration_file} />
        <Row label="अर्जदाराचा फोटो" value={d.photo} />
      </Section>

      <Section index={2} title="शैक्षणिक माहिती">
        {["१२ वी (HSC)", "पदवी", "पदव्युत्तर पदवी"].map((nm, i) => (
          d[`edu_passed_${i}`] === "yes" ? (
            <div key={i} className="border-b border-dotted border-slate-200 py-2">
              <p className="text-[13.5px] font-bold text-[#1b5e3f]">{nm}</p>
              <Row label="बोर्ड / विद्यापीठ" value={d[`edu_board_${i}`]} />
              <Row label="उत्तीर्ण वर्ष" value={d[`edu_year_${i}`]} />
              <Row label="टक्केवारी / श्रेणी" value={d[`edu_pct_${i}`]} />
              <Row label="प्रमाणपत्र" value={d[`edu_file_${i}`]} />
            </div>
          ) : <Row key={i} label={nm} value={d[`edu_passed_${i}`]} />
        ))}
      </Section>

      <Section index={3} title="संगणक साक्षरता">
        {["MSCIT / समकक्ष", "सायबर सुरक्षाबाबतचे प्रमाणपत्र", "ऑनलाईन अर्जप्रक्रिया व संगणक हाताळणी प्रमाणपत्र"].map((nm, i) => (
          d[`cl_passed_${i}`] === "yes" ? (
            <div key={i} className="border-b border-dotted border-slate-200 py-2">
              <p className="text-[13.5px] font-bold text-[#1b5e3f]">{nm}</p>
              <Row label="अभ्यासक्रमाचे नाव" value={d[`cl_name_${i}`]} />
              <Row label="उत्तीर्ण वर्ष" value={d[`cl_year_${i}`]} />
              <Row label="टक्केवारी" value={d[`cl_pct_${i}`]} />
              <Row label="प्रमाणपत्र" value={d[`cl_file_${i}`]} />
            </div>
          ) : <Row key={i} label={nm} value={d[`cl_passed_${i}`]} />
        ))}
      </Section>

      <Section index={4} title="पायाभूत सुविधा">
        <div className="border-b border-dotted border-slate-200 py-2">
          <p className="text-[13.5px] font-semibold text-slate-700">1. प्रस्तावित कार्यालयाच्या जागेचा पत्ता</p>
          <Row label="पत्ता" value={d.office_address} />
          <Row label="अक्षांश (Latitude)" value={d.office_lat} />
          <Row label="रेखांश (Longitude)" value={d.office_lng} />
        </div>
        {INFRA_ITEMS.map((item, i) => (
          <div key={i} className="border-b border-dotted border-slate-200 py-2">
            <p className="text-[13.5px] font-semibold text-slate-700">{i + 2}. {item.label}</p>
            <Row label="आहे / नाही" value={d[`infra_${i}`]} />
            {d[`infra_${i}`] === "yes" && <Row label={item.qtyLabel} value={d[`infra_qty_${i}`]} />}
            {d[`infra_${i}`] === "yes" && (i === 0 || i === 2) && <Row label="पुरावा दस्तऐवज" value={d[`infra_file_${i}`]} />}
          </div>
        ))}
      </Section>

      <Section index={5} title="आर्थिक क्षमता">
        {["गुंतवणुकीसाठी पुरेशी बँक शिल्लक/मुळात ठेव (FD) व इतर गुंतवणूक", "आर्थिक क्षमतेबाबत ऐपत (बँक सॉल्वन्सी/गॅरेंटी/तहसीलदार ऐपत दाखला)", "मागील दोन वर्षांची आयकर विवरणपत्रे ( सन 2024-25 व सन 2025-26 )", "आर्थिक शाश्वततेचे स्वघोषणापत्र"].map((nm, i) => (
          <div key={i} className="border-b border-dotted border-slate-200 py-2">
            <p className="text-[13.5px] font-semibold text-slate-700">{i + 1}. {nm}</p>
            <Row label="आहे / नाही" value={d[`fin_plan_${i}`]} />
            {d[`fin_plan_${i}`] === "yes" && <Row label="तपशील" value={d[`fin_detail_${i}`]} />}
            {d[`fin_plan_${i}`] === "yes" && <Row label="पुरावा दस्तऐवज" value={d[`fin_file_${i}`]} />}
          </div>
        ))}
      </Section>

      <Section index={6} title="अनुभव">
        <div className="border-b border-dotted border-slate-200 py-2">
          <Row label="१. CSC क्षेत्रात अनुभव" value={d.cscExp} />
          {d.cscExp === "yes" && <><Row label="CSC ID" value={d.csc_id} /><Row label="पुरावा" value={d.csc_file} /></>}
        </div>
        <div className="border-b border-dotted border-slate-200 py-2">
          <Row label="२. बँकिंग प्रतिनिधी अनुभव" value={d.bankRep} />
          {d.bankRep === "yes" && <><Row label="ID" value={d.bk_id} /><Row label="पुरावा" value={d.bk_file} /></>}
        </div>
        <div className="border-b border-dotted border-slate-200 py-2">
          <Row label="३. ई-गव्हर्नन्स अनुभव" value={d.egovExp} />
          {d.egovExp === "yes" && <><Row label="तपशील" value={d.eg_details} /><Row label="पुरावा" value={d.eg_file} /></>}
        </div>
        <div className="py-2">
          <Row label="४. महा-ई सेवा केंद्र अनुभव" value={d.prevCenter} />
          {d.prevCenter === "yes" && <><Row label="केंद्र नाव व पत्ता" value={d.pc_addr} /><Row label="पुरावा" value={d.pc_file} /></>}
        </div>
      </Section>

      <Section index={7} title="व्यवसाय आराखडा">
        <Row label="सेवा विस्तार" value={d.bp_text_0} />
        <Row label="जनजागृती" value={d.bp_text_1} />
        <Row label="आर्थिक व्यवहार्यता" value={d.bp_text_2} />
        <Row label="व्यवसाय नियोजन" value={d.bp_text_3} />
        <Row label="आराखडा दस्तऐवज" value={d.bp_pdf} />
      </Section>

      <Section index={8} title="सामाजिक समावेशकता">
        <div className="border-b border-dotted border-slate-200 py-2">
          <p className="text-[13.5px] font-semibold text-slate-700">1. महिला</p>
          <Row label="होय / नाही" value={(d.si_female || (d.gender === "स्त्री" ? "yes" : "no")) === "yes" ? "होय" : "नाही"} />
        </div>
        {SOCIAL_ITEMS.map((item, i) => (
          <div key={item.key} className="border-b border-dotted border-slate-200 py-2">
            <p className="text-[13.5px] font-semibold text-slate-700">{i + 2}. {item.label}</p>
            <Row label="आहे / नाही" value={d[item.key]} />
            {d[item.key] === "yes" && <Row label="पुराव्याची कागदपत्रे" value={d[`${item.key}_file`]} />}
          </div>
        ))}
      </Section>

      <Section index={9} title="चारित्र्य पडताळणी">
        <Row label="पोलीस ठाण्याचे नाव" value={d.cv_station} />
        <Row label="क्रमांक" value={d.cv_number} />
        <Row label="दिनांक" value={d.cv_date} />
        <Row label="पडताळणी पुरावा" value={d.cv_file} />
        <Row label="आर्थिक गैरव्यवहार समावेश" value={d.cv_fraud} />
        <Row label="काळ्या यादीत समावेश" value={d.cv_blacklist} />
        <Row label="प्रतिज्ञापत्र" value={d.cv_affidavit_file} />
      </Section>

      <Section index={10} title="अर्ज शुल्क">
        <Row label="पेमेंटची पद्धत" value={d.fee_method} />
        <Row label="UTR क्रमांक" value={d.fee_utr} />
        <Row label="पेमेंट पुरावा" value={d.fee_proof} />
      </Section>

      <Section index={11} title="कागदपत्रे (अपलोड केलेली)">
        {Object.keys(d).filter((k) => d[k] && typeof d[k] === "object" && d[k].fileId).length === 0 ? (
          <Row label="कागदपत्रे" value={null} />
        ) : (
          Object.keys(d)
            .filter((k) => d[k] && typeof d[k] === "object" && d[k].fileId)
            .map((k) => <Row key={k} label={fileLabel(k)} value={d[k]} />)
        )}
      </Section>

      {/* Final declaration */}
      <fieldset className="rounded-lg border border-dashed border-[#E0A106] bg-orange-50/40 px-4 pb-4 pt-3">
        <legend className="px-2 text-[15px] font-bold text-[#c2570a]">अंतिम घोषणा</legend>
        <label className="flex cursor-pointer items-start gap-2.5 text-[14px]">
          <input
            type="checkbox"
            checked={!!d.declaration}
            onChange={(e) => setField("declaration", e.target.checked)}
            className="mt-1 h-4 w-4 accent-[#1b5e3f]"
          />
          <span>मी वरील सर्व माहिती माझ्या माहितीप्रमाणे खरी व बरोबर आहे असे प्रतिज्ञापूर्वक जाहीर करतो/करते. चुकीची माहिती आढळल्यास कायदेशीर कारवाईस पात्र राहीन. <span className="text-[#E0A106]">*</span></span>
        </label>
        {errors.declaration && <p className="mt-2 text-[12.5px] text-[#dc2626]">{errors.declaration}</p>}
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-[14px] font-semibold text-slate-700">अर्जदाराचे संपूर्ण नाव (स्वाक्षरीसाठी) <span className="text-[#E0A106]">*</span></label>
            <input
              value={signNameVal}
              readOnly
              data-testid="preview-sign-name"
              className="w-full cursor-not-allowed rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-[14px] font-semibold text-slate-700 outline-none"
            />
            <p className="mt-1 text-[12px] text-slate-400">टप्पा १ मधील माहितीवरून आपोआप घेतले</p>
          </div>
          <div>
            <label className="mb-1.5 block text-[14px] font-semibold text-slate-700">मोबाईल नं. <span className="text-[#E0A106]">*</span></label>
            <input
              value={signMobileVal}
              readOnly
              data-testid="preview-sign-mobile"
              className="w-full cursor-not-allowed rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-[14px] font-semibold text-slate-700 outline-none"
            />
            <p className="mt-1 text-[12px] text-slate-400">टप्पा १ मधील माहितीवरून आपोआप घेतले</p>
          </div>
          <div>
            <label className="mb-1.5 block text-[14px] font-semibold text-slate-700">दिनांक</label>
            <input
              value={declDateVal}
              readOnly
              data-testid="preview-decl-date"
              className="w-full cursor-not-allowed rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-[14px] font-semibold text-slate-700 outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[14px] font-semibold text-slate-700">ठिकाण <span className="text-[#E0A106]">*</span></label>
            <input
              value={d.decl_place ?? ""}
              onChange={(e) => setField("decl_place", e.target.value)}
              data-testid="preview-decl-place"
              placeholder="उदा. सांगली"
              className={`w-full rounded-md border bg-white px-3 py-2 text-[14px] outline-none focus:border-[#1b5e3f] focus:ring-2 focus:ring-[#1b5e3f]/15 ${errors.decl_place ? "border-[#dc2626]" : "border-slate-300"}`}
            />
            {errors.decl_place && <p className="mt-1 text-[12.5px] text-[#dc2626]">{errors.decl_place}</p>}
          </div>
        </div>
      </fieldset>
    </div>
  );
}
